import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';

import { getHeadingSection } from '../remix-heading-section';

export type RemixHoverHighlightPluginState = {
	decorationSet: DecorationSet;
	highlightedPos: number | null;
};

export type RemixHoverHighlightPluginMeta = {
	/** Position of the root node to highlight. null to clear. */
	highlightPos: number | null;
	/** Root node type for table-specific styling parity with copy hover. */
	rootNodeType?: string;
};

export const remixHoverHighlightPluginKey: PluginKey<RemixHoverHighlightPluginState> =
	new PluginKey<RemixHoverHighlightPluginState>('remixHoverHighlightPlugin');

/**
 * Default class for remix hover highlight. Tables use the editor selected-node
 * class instead so they reuse the exact same table styling as copy hover.
 */
export const REMIX_HOVER_HIGHLIGHT_CLASS = 'remix-hover-highlight';

const getHeadingSectionDecorations = ({
	doc,
	headingPos,
	className,
}: {
	className: string;
	doc: PMNode;
	headingPos: number;
}): Decoration[] => {
	const headingSection = getHeadingSection(doc, headingPos);
	if (!headingSection) {
		return [];
	}

	const decorations: Decoration[] = [];
	let currentPos = headingSection.startPos;
	for (let index = headingSection.startIndex; index < headingSection.endIndex; index++) {
		const child = doc.child(index);
		decorations.push(
			Decoration.node(currentPos, currentPos + child.nodeSize, {
				class: className,
			}),
		);
		currentPos += child.nodeSize;
	}

	return decorations;
};

export function createRemixHoverHighlightPmPlugin(): SafePlugin {
	return new SafePlugin({
		key: remixHoverHighlightPluginKey,
		state: {
			init(): RemixHoverHighlightPluginState {
				return {
					highlightedPos: null,
					decorationSet: DecorationSet.empty,
				};
			},
			apply(tr, pluginState, _oldState, newState): RemixHoverHighlightPluginState {
				const meta: RemixHoverHighlightPluginMeta | undefined = tr.getMeta(
					remixHoverHighlightPluginKey,
				);

				if (meta !== undefined) {
					if (meta.highlightPos === null) {
						// Clear highlight
						return {
							highlightedPos: null,
							decorationSet: DecorationSet.empty,
						};
					}

					const pos = meta.highlightPos;
					const node = newState.doc.nodeAt(pos);
					if (!node) {
						return pluginState;
					}

					const decorationClassNames =
						meta.rootNodeType === 'table'
							? ['ak-editor-selected-node']
							: [REMIX_HOVER_HIGHLIGHT_CLASS];

					const className = decorationClassNames.join(' ');

					const headingSectionDecorations = getHeadingSectionDecorations({
						doc: newState.doc,
						headingPos: pos,
						className,
					});

					const decorations =
						headingSectionDecorations.length > 0
							? headingSectionDecorations
							: [
									Decoration.node(pos, pos + node.nodeSize, {
										class: className,
									}),
								];

					const decorationSet = DecorationSet.create(newState.doc, decorations);
					return {
						highlightedPos: pos,
						decorationSet,
					};
				}

				// Map decorations on doc changes
				if (
					pluginState.highlightedPos !== null &&
					pluginState.decorationSet !== DecorationSet.empty
				) {
					const mappedDecorationSet = pluginState.decorationSet.map(tr.mapping, newState.doc);
					if (mappedDecorationSet !== pluginState.decorationSet) {
						const mappedPos = tr.mapping.map(pluginState.highlightedPos);
						return {
							...pluginState,
							highlightedPos: mappedPos,
							decorationSet: mappedDecorationSet,
						};
					}
				}

				return pluginState;
			},
		},
		props: {
			decorations(state) {
				return remixHoverHighlightPluginKey.getState(state)?.decorationSet ?? DecorationSet.empty;
			},
		},
	});
}
